import React from 'react';
import PropTypes from 'prop-types';
import { Ic } from '../shell/Icons';
import ItemTooltip from '../itemCard/ItemTooltip';
import { relationshipVerb } from '../../utils/linkGrammar';

// #374: group heading = the relationship verb read FROM this item, direction-aware
// (an INCOMING link is flipped to its opposite so it reads correctly). A stored
// `parent` seen on the parent side reads "Child"; an incoming `derives_from` reads
// "Derived by" — instead of the raw stored verb, which named the other endpoint.
function groupLabel(rel) {
  return relationshipVerb(rel.type, rel.direction);
}

// rel.item is "<id>: <title>"; split it for the chip + title. Prefer the human
// display_id (REQ-NNNN) for the chip; fall back to a short id, never a full GUID.
function parts(rel) {
  const raw = String(rel.item || rel.targetId || '');
  const idx = raw.indexOf(': ');
  const id = idx >= 0 ? raw.slice(0, idx) : (rel.targetId || raw);
  const title = idx >= 0 ? raw.slice(idx + 2) : '';
  const human = rel.displayId && String(rel.displayId).trim();
  const chip = human
    ? human
    : (/^[A-Za-z]{2,6}-\d+/.test(id) ? id : `${String(id).slice(0, 7)}…`);
  return { chip, title, full: human || id };
}

// Same key shape ItemDetails uses for `pendingRelRemovals` — must match exactly
// so a pending mark toggled here is recognised there.
function relKey(rel) {
  return `${rel.targetId}::${rel.type}::${rel.direction}`;
}

const RelationshipsTab = ({ relationships, handleLink, handleDeleteRelationship, pendingRelRemovals }) => {
  const pending = pendingRelRemovals || new Set();
  // Stable grouping preserving first-seen order. No positional index is carried:
  // delete/undo take the `rel` object itself (handleDeleteRelationship(rel)) and
  // the row key is relKey(rel), so the original array position is never read.
  const groups = [];
  const byLabel = {};
  relationships.forEach((rel) => {
    const label = groupLabel(rel);
    if (!byLabel[label]) { byLabel[label] = []; groups.push(label); }
    byLabel[label].push(rel);
  });

  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" className="btn secondary" onClick={handleLink} style={{ marginBottom: 10 }}>
        <Ic name="plus" />Add Relationship
      </button>

      {relationships.length === 0 ? (
        <div className="t-body muted3">No relationships defined for this item.</div>
      ) : (
        groups.map((label) => (
          <div key={label} style={{ marginBottom: 12 }}>
            <div className="t-over" style={{ marginBottom: 4 }}>{label} · {byLabel[label].length}</div>
            {byLabel[label].map((rel) => {
              const p = parts(rel);
              const isPending = pending.has(relKey(rel));
              return (
                <div
                  className="lrow"
                  // A stable identity (not the array index) — a background
                  // relationships refetch racing a user's in-flight hover
                  // reordering/reshuffling this list would otherwise remount
                  // the DOM node under an already-positioned cursor. Browsers
                  // only fire mouseenter/mouseover on actual pointer motion,
                  // so a swapped-in replacement node silently never receives
                  // one — ItemTooltip's hover-triggered mini-peek just doesn't
                  // open, no error. relKey already exists for pendingRelRemovals
                  // matching; reuse it here as the key too.
                  key={relKey(rel)}
                  style={{ padding: '7px 0', opacity: isPending ? 0.5 : 1 }}
                >
                  {/* #183: linked item — hover peek; click the id hyperlinks to it
                      (mobile: tap opens the card, the ↗ icon navigates). */}
                  <ItemTooltip item={{ id: rel.targetId, display_id: rel.displayId, title: p.title, type: rel.type }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                      <span className="idchip" title={p.full}>{p.chip}</span>
                      <span
                        className="t-body"
                        style={{
                          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          textDecoration: isPending ? 'line-through' : 'none',
                        }}
                      >
                        {p.title || '—'}
                      </span>
                      {isPending && <span className="t-over" style={{ flexShrink: 0 }}>Pending removal</span>}
                    </span>
                  </ItemTooltip>
                  {isPending ? (
                    <button type="button"
                      className="btn ghost sm"
                      aria-label="Undo removing this relationship"
                      onClick={() => handleDeleteRelationship(rel)}
                      style={{ fontSize: 12, lineHeight: 1 }}
                    >
                      Undo
                    </button>
                  ) : (
                    <button type="button"
                      className="btn ghost sm"
                      aria-label="Delete this relationship"
                      onClick={() => handleDeleteRelationship(rel)}
                      style={{ fontSize: 14, lineHeight: 1 }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};

RelationshipsTab.propTypes = {
  relationships: PropTypes.array.isRequired,
  handleLink: PropTypes.func.isRequired,
  handleDeleteRelationship: PropTypes.func.isRequired,
  pendingRelRemovals: PropTypes.instanceOf(Set),
};

export default RelationshipsTab;
